import React, { useRef, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  Modal,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { UpdateDeviceConfig } from "../Api";
import {
  AllFormData,
  FormFieldMap,
  functionTypeOptions,
  typeValues,
} from "./type";
import PreviewLogs from "./PreviewLogs";
interface Props {
  deviceId: string;
  setSetting: React.Dispatch<React.SetStateAction<string>>;
}

const requiredFieldMap: Record<keyof FormFieldMap, (keyof AllFormData)[]> = {
  readCoils: ["slave_id", "baud", "parity", "address", "quantity", "type"],
  readDiscreteInputs: ["slave_id", "baud", "parity", "address", "quantity", "type"],
  readInputRegisters: ["slave_id", "baud", "parity", "address", "quantity", "type"],
  readHoldingRegisters: ["slave_id", "baud", "parity", "address", "quantity", "type"],
  writeSingleCoil: ["slave_id", "address", "value", "type"],
  writeSingleRegister: ["slave_id", "address", "value", "type"],
  writeMultipleCoils: ["slave_id", "address", "values", "type"],
  writeMultipleRegisters: ["slave_id", "address", "values", "type"],
};

const commandMap: Record<string, string> = {
  readCoils: "read_coils",
  readDiscreteInputs: "read_discrete_inputs",
  readHoldingRegisters: "read_holding_registers",
  readInputRegisters: "read_input_registers",
  writeSingleCoil: "write_single_coil",
  writeSingleRegister: "write_single_register",
  writeMultipleCoils: "write_multiple_coils",
  writeMultipleRegisters: "write_multiple_registers",
};

const DeviceSettings = ({ deviceId, setSetting }: Props) => {
  const payloadRef = useRef<HTMLDivElement>(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "error" | "info" | "warning",
  });
  const [selectedOption, setSelectedOption] = useState<string>("readCoils");
  const [formData, setFormData] = useState<AllFormData>({
    type: typeValues[0].value,
  });
  const [open, setOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(false);
  const [mode, setMode] = useState<"Modbus-RTU" | "Modbus-TCP" | "Counter">("Modbus-RTU");
  const [counterNumber, setCounterNumber] = useState<string>("");

  // const [selectedTypeOption, setSelectedTypeOption] = useState<string>("float");

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedOption(event.target.value);
    setFormData({});

    setFormData({ type: typeValues[0].value });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as "Modbus-RTU" | "Modbus-TCP" | "Counter";
    setMode(newMode);
    // Clear conflicting fields when mode changes
    setFormData((prev) => {
      const newData = { ...prev };
      if (newMode === "Modbus-TCP") {
        delete newData.baud;
        delete newData.parity;
      } else if (newMode === "Modbus-RTU") {
        delete newData.ip;
        delete newData.port;
      }
      return newData;
    });
  };

  const renderFormFields = () => {
    if (mode === "Counter") {
      return (
        <Box
          mt={1}
          p={3}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box ml={1}>
            <Typography variant="body1" fontWeight="500">Read Counter</Typography>
            <Typography variant="body2" color="text.secondary">
              Fetch the current value from the IoT device counter. Clicking Submit will send the "i2c_read_counter,&lt;number&gt;" command.
            </Typography>
          </Box>
          <TextField
            fullWidth
            margin="normal"
            variant="outlined"
            size="small"
            label="Counter Number"
            type="number"
            value={counterNumber}
            onChange={(e) => setCounterNumber(e.target.value)}
            helperText="e.g., 3  →  i2c_read_counter,3"
          />
        </Box>
      );
    }

    const fieldProps = {
      fullWidth: true,
      margin: "normal" as const,
      variant: "outlined" as const,
      size: "small" as const,
    };

    switch (selectedOption) {
      case "readCoils":
      case "readHoldingRegisters":
      case "readInputRegisters":
      case "readDiscreteInputs":
        return (
          <>
            <TextField
              {...fieldProps}
              label="Slave ID"
              type="number"
              value={formData.slave_id || ""}
              onChange={(e) => handleInputChange("slave_id", e.target.value)}
            />
            {mode === "Modbus-RTU" ? (
              <>
                <TextField
                  {...fieldProps}
                  label="Baud"
                  type="number"
                  value={formData.baud || ""}
                  onChange={(e) => handleInputChange("baud", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  label="Parity"
                  value={formData.parity || ""}
                  onChange={(e) => handleInputChange("parity", e.target.value)}
                />
              </>
            ) : (
              <>
                <TextField
                  {...fieldProps}
                  label="IP"
                  value={formData.ip || ""}
                  onChange={(e) => handleInputChange("ip", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  label="Port"
                  type="number"
                  value={formData.port || ""}
                  onChange={(e) => handleInputChange("port", e.target.value)}
                />
              </>
            )}
            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel id="select-label">Function Type</InputLabel>
              <Select
                placeholder="Select Type"
                fullWidth={true}
                labelId="select-label"
                value={formData.type || ""}
                label="Function Type"
                onChange={(e) => handleInputChange("type", e.target.value)}
                sx={{ color: "black" }}
              >
                {typeValues.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography fontWeight="bold">{option.label}</Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              {...fieldProps}
              label="Address"
              value={formData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            <TextField
              type="number"
              {...fieldProps}
              label="Quantity"
              value={formData.quantity || ""}
              onChange={(e) => handleInputChange("quantity", e.target.value)}
            />
          </>
        );

      case "writeSingleCoil":
        return (
          <>
            <TextField
              type="number"
              {...fieldProps}
              label="Slave ID"
              value={formData.slave_id || ""}
              onChange={(e) => handleInputChange("slave_id", e.target.value)}
            />
            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel id="select-label">Function Type</InputLabel>
              <Select
                placeholder="Select Type"
                fullWidth={true}
                labelId="select-label"
                value={formData.type || ""}
                label="Function Type"
                onChange={(e) => handleInputChange("type", e.target.value)}
                sx={{ color: "black" }}
              >
                {typeValues.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography fontWeight="bold">{option.label}</Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              {...fieldProps}
              label="Address"
              value={formData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            <TextField
              {...fieldProps}
              label="Value (0 or 1)"
              value={formData.value || ""}
              onChange={(e) => handleInputChange("value", e.target.value)}
            />
          </>
        );

      case "writeSingleRegister":
        return (
          <>
            <TextField
              type="number"
              {...fieldProps}
              label="Slave ID"
              value={formData.slave_id || ""}
              onChange={(e) => handleInputChange("slave_id", e.target.value)}
            />
            {mode === "Modbus-RTU" ? (
              <>
                <TextField
                  {...fieldProps}
                  label="Baud"
                  type="number"
                  value={formData.baud || ""}
                  onChange={(e) => handleInputChange("baud", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  label="Parity"
                  value={formData.parity || ""}
                  onChange={(e) => handleInputChange("parity", e.target.value)}
                />
              </>
            ) : (
              <>
                <TextField
                  {...fieldProps}
                  label="IP"
                  value={formData.ip || ""}
                  onChange={(e) => handleInputChange("ip", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  type="number"
                  label="Port"
                  value={formData.port || ""}
                  onChange={(e) => handleInputChange("port", e.target.value)}
                />
              </>
            )}
            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel id="select-label">Function Type</InputLabel>
              <Select
                placeholder="Select Type"
                fullWidth={true}
                labelId="select-label"
                value={formData.type || ""}
                label="Function Type"
                onChange={(e) => handleInputChange("type", e.target.value)}
                sx={{ color: "black" }}
              >
                {typeValues.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography fontWeight="bold">{option.label}</Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              {...fieldProps}
              label="Address"
              value={formData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            <TextField
              type="number"
              {...fieldProps}
              label="Quantity"
              value={formData.quantity || ""}
              onChange={(e) => handleInputChange("quantity", e.target.value)}
            />
            <TextField
              {...fieldProps}
              label="Value (uint16)"
              value={formData.value || ""}
              onChange={(e) => handleInputChange("value", e.target.value)}
            />
          </>
        );

      case "writeMultipleCoils":
        return (
          <>
            <TextField
              type="number"
              {...fieldProps}
              label="Slave ID"
              value={formData.slave_id || ""}
              onChange={(e) => handleInputChange("slave_id", e.target.value)}
            />
            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel id="select-label">Function Type</InputLabel>
              <Select
                placeholder="Select Type"
                fullWidth={true}
                labelId="select-label"
                value={formData.type || ""}
                label="Function Type"
                onChange={(e) => handleInputChange("type", e.target.value)}
                sx={{ color: "black" }}
              >
                {typeValues.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography fontWeight="bold">{option.label}</Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              {...fieldProps}
              label="Address"
              value={formData.address || ""}
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            <TextField
              {...fieldProps}
              value={formData.values || ""}
              label="Values (comma-separated 0/1)"
              helperText="e.g., 1,0,1,1"
              onChange={(e) => handleInputChange("values", e.target.value)}
            />
          </>
        );

      case "writeMultipleRegisters":
        return (
          <>
            <TextField
              type="number"
              {...fieldProps}
              value={formData.slave_id || ""}
              label="Slave ID"
              onChange={(e) => handleInputChange("slave_id", e.target.value)}
            />
            {mode === "Modbus-RTU" ? (
              <>
                <TextField
                  {...fieldProps}
                  label="Baud"
                  type="number"
                  value={formData.baud || ""}
                  onChange={(e) => handleInputChange("baud", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  label="Parity"
                  value={formData.parity || ""}
                  onChange={(e) => handleInputChange("parity", e.target.value)}
                />
              </>
            ) : (
              <>
                <TextField
                  {...fieldProps}
                  label="IP"
                  value={formData.ip || ""}
                  onChange={(e) => handleInputChange("ip", e.target.value)}
                />
                <TextField
                  {...fieldProps}
                  type="number"
                  label="Port"
                  value={formData.port || ""}
                  onChange={(e) => handleInputChange("port", e.target.value)}
                />
              </>
            )}
            <FormControl fullWidth sx={{ marginTop: 1 }}>
              <InputLabel id="select-label">Function Type</InputLabel>
              <Select
                placeholder="Select Type"
                fullWidth={true}
                labelId="select-label"
                value={formData.type || ""}
                label="Function Type"
                onChange={(e) => handleInputChange("type", e.target.value)}
                sx={{ color: "black" }}
              >
                {typeValues.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography fontWeight="bold">{option.label}</Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography> */}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              {...fieldProps}
              value={formData.address || ""}
              label="Address"
              onChange={(e) => handleInputChange("address", e.target.value)}
            />
            <TextField
              {...fieldProps}
              value={formData.values || ""}
              label="Values (comma-separated uint16)"
              helperText="e.g., 123,456,789"
              onChange={(e) => handleInputChange("values", e.target.value)}
            />
            <TextField
              type="number"
              {...fieldProps}
              label="Quantity"
              value={formData.quantity || ""}
              onChange={(e) => handleInputChange("quantity", e.target.value)}
            />
          </>
        );

      default:
        return null;
    }
  };

  const buildPayload = () => {
    if (mode === "Counter") {
      return `i2c_read_counter,${counterNumber}`;
    }

    const {
      slave_id,
      ip,
      port,
      address,
      quantity,
      value,
      values,
      type,
      parity,
      baud,
    } = formData;

    const cmd = commandMap[selectedOption];
    if (!cmd) return "";

    let payload = `${cmd},${slave_id}`;

    // Assuming you have a separate "mode" variable to distinguish RTU or TCP mode
    if (mode === "Modbus-RTU") {
      payload += `,${baud ?? ""},${parity || "none"}`;
    } else if (mode === "Modbus-TCP") {
      payload += `,${ip ?? ""},${port ?? ""}`;
    }

    payload += `,${address ?? ""},${type || ""}`;

    if (quantity) payload += `,${quantity}`;
    if (value !== undefined && value !== null) payload += `,${value}`;
    if (values !== undefined && values !== null) {
      const parsedValues = values
        .split(",")
        .map((v) => v.trim())
        .map((v) => (isNaN(Number(v)) ? v : Number(v)));
      payload += `,[${parsedValues}]`;
    }

    return payload;
  };

  /**
   * Validates the form based on required fields.
   * Displays a warning toast if any required field is missing.
   */
  const getFilteredRequiredFields = (): string[] => {
    if (mode === "Counter") {
      // Validate counter number separately
      return [];
    }

    let baseFields =
      requiredFieldMap[selectedOption as keyof FormFieldMap] || [];

    if (mode === "Modbus-TCP") {
      // Remove RTU fields
      baseFields = baseFields.filter(
        (field) => field !== "baud" && field !== "parity"
      );
      // Add TCP required fields explicitly (even if not in base)
      if (!baseFields.includes("ip")) baseFields.push("ip");
      if (!baseFields.includes("port")) baseFields.push("port");
    }

    return baseFields;
  };

  const validateForm = (): boolean => {
    if (mode === "Counter") {
      if (!counterNumber || counterNumber.trim() === "") {
        showToast("Please enter a Counter Number", "warning");
        return false;
      }
      return true;
    }

    const requiredFields = getFilteredRequiredFields();

    for (const field of requiredFields) {
      const value = formData[field as keyof typeof formData];

      if (
        value === undefined ||
        value === null ||
        value.toString().trim() === ""
      ) {
        showToast(`Please fill the required field: ${field}`, "warning");
        return false;
      }
    }

    return true;
  };

  /**
   * Submits the form data.
   * Shows a success message on successful submission,
   * or an error message if submission fails.
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = buildPayload();

    try {
      setSubmitLoading(true);
      await UpdateDeviceConfig(payload, deviceId);
      showToast("Submitted successfully!", "success");
      // setFormData({ type: typeValues[0].value });
      setRefreshHistory(!refreshHistory);
    } catch (error) {
      console.error("Submission failed:", error);
      showToast("Submission failed", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  /**
   * Copies the preview payload text to the clipboard.
   * Displays a success or error toast based on the result.
   */
  const handleCopy = () => {
    const text = payloadRef.current?.textContent;
    if (text) {
      navigator.clipboard
        .writeText(text)
        .then(() => showToast("Copied to clipboard!", "success"))
        .catch((err) => showToast("Failed to copy", "error"));
    }
  };

  /**
   * Displays toast messages (info, success, warning, or error)
   * at the top-right of the screen.
   *
   * @param {string} message - The message to display.
   * @param {'info' | 'success' | 'warning' | 'error'} severity - The severity of the message.
   */
  const showToast = (
    message: string,
    severity: typeof toast.severity = "info"
  ) => {
    setToast({ open: true, message, severity });
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => showToast("Copied to clipboard!", "success"))
      .catch(() => showToast("Failed to copy", "error"));
  };

  return (
    <Box
      sx={{
        p: 2,
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: { sx: "column", md: "row" },
        gap: "1rem",
      }}
    >
      <Box width={{ sx: "100%", md: "70%" }}>
        <Grid container spacing={2} alignItems="center">
          {mode !== "Counter" && (
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel id="select-label">Function Type</InputLabel>
                <Select
                  labelId="select-label"
                  value={selectedOption}
                  label="Function Type"
                  onChange={handleChange}
                  sx={{ minHeight: "70px" }}
                >
                  {functionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography fontWeight="bold">{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box mt={1} mb={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Mode
            </FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={handleModeChange}
            >
              <FormControlLabel
                value="Modbus-RTU"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Modbus-RTU</Typography>}
              />
              <FormControlLabel
                value="Modbus-TCP"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Modbus-TCP</Typography>}
              />
              <FormControlLabel
                value="Counter"
                control={<Radio size="small" />}
                label={<Typography variant="body2">Counter</Typography>}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box mt={3}>{renderFormFields()}</Box>

        <Box display="flex" gap={2} mt={2} justifyContent={"space-between"}>
          <Box display={"flex"} alignItems={"center"} gap={"1rem"}>
            <Button variant="outlined" size="small" onClick={handleOpen}>
              Preview Payload
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSubmit}
              size="small"
              loading={submitLoading}
            >
              Submit
            </LoadingButton>
          </Box>
          <Button
            onClick={() => setSetting("")}
            variant="contained"
            size="small"
          >
            Go Back
          </Button>
        </Box>
      </Box>
      <Box width={{ sx: "100%", md: "30%" }} p={1}>
        <PreviewLogs
          handleCopyText={handleCopyText}
          deviceID={deviceId}
          refreshHistory={refreshHistory}
        />
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: { xs: "90%", sm: "90%" },
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: 300,
            maxWidth: 600,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            overflow: "auto",
            maxHeight: "80vh",
          }}
        >
          <Typography variant="h6" gutterBottom>
            JSON Payload Preview
          </Typography>
          <Box
            ref={payloadRef}
            sx={{
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: 14,
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
            }}
          >
            {buildPayload()}
          </Box>
          <Box
            textAlign="right"
            mt={2}
            display="flex"
            justifyContent="flex-end"
            gap={1}
          >
            <Button onClick={handleCopy} size="small" variant="outlined">
              Copy
            </Button>
            <Button onClick={handleClose} size="small">
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeviceSettings;
